import { type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, ChevronDown, Clock, Hotel, Loader2, RefreshCw, Sparkles, Wrench } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/cn';
import { HotelOccupancyBoard } from '@/components/hotels/hotel-occupancy-board';
import { AllocationPlanTable } from '@/components/allocation/allocation-plan-table';
import type { IHotelBoard, IHotelOccupancy } from '@/components/hotels/types';
import type { AllocationPlan, BookAllResult } from '@/lib/bulk-allocation/types';

// Scope-neutral allocation workspace — the ONE component behind both the disruption
// "Hotels" tab and the case-detail "Allocation" tab (cases↔disruptions convergence).
// Occupancy-first: the `HotelOccupancyBoard` "who's where" hero leads; the allocation
// ACTIONS (one-click allocate, meal vouchers, the review-then-book plan) live in a
// contextual "Accommodation tools" drawer that auto-collapses once nothing's pending.
// Everything scope-specific is injected: the board source, the plan controller, and
// slots for the case-only wave/demand bar, the event action/voucher bars, and the
// manual assignment board. Layout follows §18.1 (fixed chrome, contained scroll).

/** Normalized plan state both scopes adapt to. The disruption builds a SHARED,
 *  server-stored plan (multi-operator, WS-live); the case builds a transient one —
 *  both reduce to this shape so the workspace renders one plan card. */
export interface AllocationPlanController {
  plan: AllocationPlan | null;
  results: BookAllResult[] | null;
  planning: boolean;
  booking: boolean;
  buildPlan: () => void;
  bookAll: () => void;
  buildError?: boolean;
  bookError?: boolean;
  /** Disruption-only freshness/attribution cue (who built it, how long ago). */
  attribution?: string | undefined;
  stale?: boolean | undefined;
  /** Per-scope button copy. */
  buildLabel?: string;
  rebuildLabel?: string;
  /** Per-scope empty-state copy under the plan card. */
  emptyHint?: ReactNode;
}

export interface AllocationWorkspaceProps {
  board: { data?: IHotelBoard | undefined; isLoading: boolean; isError: boolean };
  controller: AllocationPlanController;
  /** Case-only wave/demand approval bar (rendered above the tools drawer). */
  waveBar?: ReactNode;
  /** One-click allocate bar inside the tools drawer (event scope). */
  actionBar?: ReactNode;
  /** Meal-voucher bar inside the tools drawer (event scope). */
  voucherBar?: ReactNode;
  /** Manual per-PNR assignment board (collapsible exception path). */
  manualBoard?: ReactNode;
  /** MASS ground transport (additive): the shuttle plan editor, rendered inside the
   *  tools drawer under the action bars; `toolsOpenHint` opens the drawer for it. */
  transferPanel?: ReactNode;
  toolsOpenHint?: boolean | undefined;
  /** Pass-through to the occupancy board (deep-linked hotel focus + per-card extra). */
  initialHotel?: string | undefined;
  renderHotelExtra?: ((hotel: IHotelOccupancy) => ReactNode) | undefined;
}

export function AllocationWorkspace({
  board,
  controller,
  waveBar,
  actionBar,
  voucherBar,
  manualBoard,
  transferPanel,
  toolsOpenHint,
  initialHotel,
  renderHotelExtra,
}: AllocationWorkspaceProps) {
  const { t } = useTranslation('cases');
  const pending = board.data?.pendingPnrs ?? 0;
  const hotelsCount = board.data?.hotels.length ?? 0;

  const { plan, results, planning, booking, attribution, stale } = controller;
  const bookable = !!plan && plan.plan.length > 0 && !results;

  // Tools drawer: auto-open while there's outstanding demand or a plan in flight;
  // the operator can pin it open/closed. Once everything's allocated it tucks away.
  const [toolsOverride, setToolsOverride] = useState<boolean | null>(null);
  const toolsOpen = toolsOverride ?? (pending > 0 || !!plan || !!toolsOpenHint);

  // Manual board (exception path) — collapsed by default so the bulk flow is the
  // obvious default; opened to hand-place a group (e.g. a plan gap).
  const [manualOpen, setManualOpen] = useState(false);

  return (
    <div className="flex h-full min-h-0 flex-col gap-3 p-4 sm:p-6">
      {/* Board hero — "who's where". The live allocation status sits below it. */}
      <section className="flex min-h-0 flex-1 flex-col gap-2" aria-label={t('workspace.occupancyLabel')}>
        <div className="min-h-0 flex-1">
          <HotelOccupancyBoard
            board={board.data}
            isLoading={board.isLoading}
            isError={board.isError}
            initialHotel={initialHotel}
            renderHotelExtra={renderHotelExtra}
          />
        </div>
        {pending > 0 ? (
          <div className="flex shrink-0 justify-end">
            <span
              className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800 dark:bg-amber-900/30 dark:text-amber-300"
              data-testid="alloc-status-pending"
            >
              {t('workspace.pending', { count: pending })}
            </span>
          </div>
        ) : hotelsCount > 0 ? (
          <div className="flex shrink-0 justify-end">
            <span
              className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300"
              data-testid="alloc-status-complete"
            >
              <Check className="h-3 w-3" aria-hidden /> {t('workspace.allAllocated')}
            </span>
          </div>
        ) : null}
      </section>

      {/* Case-only wave/demand approval controls (no event analog). */}
      {waveBar ?? null}

      {/* Accommodation tools — contextual drawer. Houses the one-click allocate, meal
          vouchers, and the review-then-book plan. Collapsed once nothing's pending. */}
      <div className="shrink-0 rounded-lg border border-border" data-testid="accommodation-tools">
        <button
          type="button"
          onClick={() => setToolsOverride(!toolsOpen)}
          aria-expanded={toolsOpen}
          className="flex w-full items-center gap-2 px-3 py-2 text-sm hover:bg-muted/40"
          data-testid="accommodation-tools-toggle"
        >
          <ChevronDown
            className={cn('h-4 w-4 shrink-0 transition-transform', toolsOpen ? '' : '-rotate-90')}
            aria-hidden
          />
          <span className="font-medium">{t('workspace.toolsTitle')}</span>
          <span className="text-xs text-muted-foreground">
            {pending > 0
              ? t('workspace.toolsPending', { count: pending })
              : t('workspace.toolsIdle')}
          </span>
        </button>

        {toolsOpen ? (
          <div className="max-h-[46vh] space-y-3 overflow-y-auto border-t border-border p-3">
            {actionBar ?? null}
            {voucherBar ?? null}
            {transferPanel ?? null}

            <Card>
              <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2 pb-2">
                <div className="flex flex-wrap items-center gap-2">
                  <CardTitle className="flex items-center gap-2 text-base">
                    <Hotel className="h-4 w-4 text-primary" aria-hidden />
                    {t('workspace.planTitle')}
                  </CardTitle>
                  {attribution ? (
                    <span
                      className={cn(
                        'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium',
                        stale
                          ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300'
                          : 'bg-muted text-muted-foreground',
                      )}
                      data-testid="event-plan-age"
                    >
                      <Clock className="h-3 w-3" aria-hidden />
                      {attribution}
                      {stale ? t('workspace.staleSuffix') : ''}
                    </span>
                  ) : null}
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    variant={plan ? 'outline' : 'default'}
                    onClick={controller.buildPlan}
                    disabled={planning || booking}
                    data-testid="event-build-plan"
                  >
                    {planning ? (
                      <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" aria-hidden />
                    ) : plan ? (
                      <RefreshCw className="mr-1 h-3.5 w-3.5" aria-hidden />
                    ) : (
                      <Sparkles className="mr-1 h-3.5 w-3.5" aria-hidden />
                    )}
                    {plan
                      ? controller.rebuildLabel ?? t('workspace.rebuild')
                      : controller.buildLabel ?? t('workspace.build')}
                  </Button>
                  {bookable ? (
                    <Button size="sm" onClick={controller.bookAll} disabled={booking} data-testid="event-book-all">
                      {booking ? (
                        <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" aria-hidden />
                      ) : (
                        <Hotel className="mr-1 h-3.5 w-3.5" aria-hidden />
                      )}
                      {t('workspace.bookAll', { count: plan!.totals.groups })}
                    </Button>
                  ) : null}
                </div>
              </CardHeader>
              <CardContent className="space-y-3 text-sm">
                {controller.buildError ? (
                  <p className="text-destructive" data-testid="event-plan-error">
                    {t('workspace.buildError')}
                  </p>
                ) : null}
                {controller.bookError ? (
                  <p className="text-destructive" data-testid="event-book-error">
                    {t('workspace.bookError')}
                  </p>
                ) : null}

                {plan ? (
                  <AllocationPlanTable
                    plan={plan}
                    results={results}
                    isBooking={booking}
                    maxHeightClass="max-h-[48vh]"
                  />
                ) : planning ? (
                  <div className="flex items-center gap-2 py-6 text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> {t('workspace.building')}
                  </div>
                ) : (
                  controller.emptyHint ?? null
                )}
              </CardContent>
            </Card>
          </div>
        ) : null}
      </div>

      {/* Manual per-PNR board — secondary, collapsed exception path. */}
      {manualBoard ? (
        <div className="shrink-0 rounded-lg border border-border" data-testid="manual-allocation">
          <button
            type="button"
            onClick={() => setManualOpen((v) => !v)}
            aria-expanded={manualOpen}
            className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-muted-foreground hover:bg-muted/40"
          >
            <ChevronDown
              className={cn('h-4 w-4 shrink-0 transition-transform', manualOpen ? '' : '-rotate-90')}
              aria-hidden
            />
            <Wrench className="h-3.5 w-3.5 shrink-0" aria-hidden />
            <span className="font-medium">{t('workspace.manualTitle')}</span>
          </button>
          {manualOpen ? (
            <div className="border-t border-border p-3">
              <p className="mb-3 text-xs text-muted-foreground">{t('workspace.manualHint')}</p>
              <div className="h-[28rem] min-h-0">{manualBoard}</div>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
